import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import {
  ReviewQueueApi,
  type ReviewQueueRow,
  type ReviewResolution,
} from "../api/reviewQueue";
import logger from "../analytics/logger";
import { fmtDate } from "../utils/datetime";

// APPROVED/REJECTED drive the REAL certification through the manual-override
// path — issuing or rejecting the certificate and emailing the creator. Not
// undoable from this screen, so both require a confirm step.
const TERMINAL: ReviewResolution[] = ["APPROVED", "REJECTED"];

type Props = {
  row: ReviewQueueRow | null;
  reviewers: string[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

export default function ReviewQueueModal({
  row,
  reviewers,
  isOpen,
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const [reviewer, setReviewer] = useState("");
  const [resolution, setResolution] = useState<ReviewResolution>("NEEDS_REVIEW");
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed every time the modal opens so a cancelled edit is discarded rather
  // than leaking into the next row the user clicks.
  useEffect(() => {
    if (!isOpen || !row) return;
    setReviewer(row.reviewer ?? "");
    setResolution(row.resolution ?? "NEEDS_REVIEW");
    setNotes(row.notes ?? "");
    setConfirming(false);
  }, [isOpen, row]);

  if (!row) return null;

  const isTerminal = TERMINAL.includes(resolution);

  async function save() {
    if (!row) return;
    if (isTerminal && !confirming) {
      setConfirming(true);
      return;
    }
    setSaving(true);
    try {
      await ReviewQueueApi.update(row.work_id, {
        reviewer: reviewer || null,
        notes: notes || null,
        resolution,
      });
      toast({ status: "success", title: "Review saved" });
      onSaved();
      onClose();
    } catch (e) {
      logger.error("review_queue_save_failed", e);
      toast({ status: "error", title: "Could not save review" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{row.title || "(untitled)"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Text fontSize="sm" color="gray.600">
              {row.type ?? "—"} · {row.profile_name ?? "—"} · entered review{" "}
              {fmtDate(row.created_at)}
            </Text>

            <FormControl>
              <FormLabel>Reviewer</FormLabel>
              <Select
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
                placeholder="Unassigned"
              >
                {reviewers.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Resolution</FormLabel>
              <Select
                value={resolution}
                onChange={(e) => {
                  setResolution(e.target.value as ReviewResolution);
                  setConfirming(false);
                }}
              >
                <option value="NEEDS_REVIEW">Leave as needs review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </FormControl>

            {confirming && (
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  This sets the certification to{" "}
                  {resolution === "APPROVED" ? "APPROVED" : "REJECTED"} and emails
                  the creator. Click Confirm to proceed.
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme={confirming ? "orange" : "blue"}
            isLoading={saving}
            onClick={save}
          >
            {confirming ? "Confirm" : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
